/**
 * Browser-translation compatibility shim.
 *
 * Tools like Google Translate / Chrome's built-in "Translate this page" mutate
 * the DOM by wrapping text nodes in <font> elements. React keeps its own virtual
 * representation of the tree, so when React later re-renders and tries to
 * remove/insert a node that the translator has moved, the browser throws:
 *
 *   "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node."
 *   "Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node."
 *
 * This unmounts the React tree (blank screen / ErrorBoundary). The fix below
 * makes removeChild / insertBefore resilient: if the target node was moved out
 * from under React by the translator, we gracefully no-op / append instead of
 * throwing. This is the well-known, community-recommended workaround
 * (see facebook/react#11538) and does not affect normal React operation.
 */
export function installTranslationDomGuard() {
  if (typeof Node !== 'function' || !Node.prototype) return;
  if (Node.prototype.__llosTranslationGuard) return;
  Node.prototype.__llosTranslationGuard = true;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      // The translator detached/moved this node — treat removal as already done.
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node was moved by the translator — append instead of throwing.
      return originalInsertBefore.call(this, newNode, null);
    }
    return originalInsertBefore.apply(this, arguments);
  };
}
